import deep from 'deep-get-set';
import { createSelector } from 'reselect';

import { deepEquals } from '@src/utils';

// Actions
const VIDEO_COMPILATION_LOAD = 'VIDEO_COMPILATION_LOAD';
const SET_SELECTED_VIDEO_ID = 'SET_SELECTED_VIDEO_ID';
const SET_FILTER_TAGS = 'SET_FILTER_TAGS';
const SET_CTA_LABEL = 'SET_CTA_LABEL';
const SET_CTA_LINK = 'SET_CTA_LINK';
const SET_CTA_DESCRIPTION = 'SET_CTA_DESCRIPTION';
const SET_AUTO_INCLUDE = 'SET_AUTO_INCLUDE';
const SET_COMPILATION_PAGE_ENABLED = 'SET_COMPILATION_PAGE_ENABLED';
const SET_COMPILATION_PAGE_WEB_LINK = 'SET_COMPILATION_PAGE_WEB_LINK';
const SET_COMPILATION_EDIT_MEDIA = 'SET_COMPILATION_EDIT_MEDIA';
const SET_COMPILATION_LOADING = 'SET_COMPILATION_LOADING';
const SET_COMPILATION_AUTO_PLAY = 'SET_COMPILATION_AUTO_PLAY';
const SET_COMPILATION_PRIMARY_COLOR = 'SET_COMPILATION_PRIMARY_COLOR';
const SET_COMPILATION_PAGE_TITLE = 'SET_COMPILATION_PAGE_TITLE';
const SET_LOGO_URL = 'SET_LOGO_URL';

export const TOGGLE_EDIT_PUBLISHED_VIDEOS_MODE = 'TOGGLE_EDIT_PUBLISHED_VIDEOS_MODE';
export const SET_IS_EDITING_COMPILATION = 'SET_IS_EDITING_COMPILATION';
export const SET_API_LOADED_INPUTS = 'SET_API_LOADED_INPUTS';
export const SET_MEDIA = 'SET_MEDIA';
export const REMOVE_ONE_VIDEO = 'REMOVE_ONE_VIDEO';

// Initial State
const initialState = {
  id: '',
  title: '',
  autoInclude: true,
  ctaLabel: 'Submit Your Own Video!',
  ctaLink: '',
  ctaDescription: null,
  enabled: true,
  linkClicks: 0,
  pageLink: '',
  createdAt: '',
  updatedAt: '',
  deleted: false,
  campaignId: null,
  lastReceived: '',
  websiteLink: '',
  campaignName: '',
  campaignBranding: {},
  media: [],
  selectedMediaId: '',
  editingMedia: {},
  allTags: [],
  filterTags: [],
  isLoading: false,
  autoPlay: true,
  primaryColor: null,
  meta: {},
  logoUrl: '',
  isEditPublishedVideosMode: false,
  editingCompilationPageLink: undefined,
  apiLoadedInputs: {
    title: undefined,
    ctaLabel: undefined,
    ctaLink: undefined,
    ctaDescription: undefined,
    logoUrl: undefined,
    primaryColor: undefined,
    media: undefined,
  },
};

// Reducers
export function videoCompilationPageReducer(state = initialState, action) {
  switch (action.type) {
    case VIDEO_COMPILATION_LOAD: {
      const newState = {
        ...state,
        ...action.payload.data,
        meta: action.payload.meta,
      };
      return newState;
    }

    case SET_SELECTED_VIDEO_ID: {
      const newState = {
        ...state,
        selectedMediaId: action.mediaId,
      };
      return newState;
    }

    case SET_FILTER_TAGS: {
      const newState = {
        ...state,
        filterTags: [...action.tagIds],
      };
      return newState;
    }

    case SET_CTA_LABEL: {
      const newState = {
        ...state,
        ctaLabel: action.label,
      };
      return newState;
    }

    case SET_CTA_LINK: {
      const newState = {
        ...state,
        ctaLink: action.link,
      };
      return newState;
    }

    case SET_CTA_DESCRIPTION: {
      const newState = {
        ...state,
        ctaDescription: action.desc,
      };
      return newState;
    }

    case SET_AUTO_INCLUDE: {
      const newState = {
        ...state,
        autoInclude: action.value,
      };

      return newState;
    }

    case SET_COMPILATION_PAGE_ENABLED: {
      const newState = {
        ...state,
        enabled: action.value,
      };

      return newState;
    }

    case SET_COMPILATION_PAGE_WEB_LINK: {
      const newState = {
        ...state,
        websiteLink: action.link,
      };

      return newState;
    }

    case SET_COMPILATION_EDIT_MEDIA: {
      const newState = {
        ...state,
        editingMedia: action.media,
      };

      return newState;
    }

    case SET_COMPILATION_LOADING: {
      const newState = {
        ...state,
        isLoading: action.value,
      };

      return newState;
    }

    case SET_COMPILATION_AUTO_PLAY: {
      const newState = {
        ...state,
        autoPlay: action.value,
      };

      return newState;
    }

    case SET_COMPILATION_PRIMARY_COLOR: {
      const newState = {
        ...state,
        primaryColor: action.value,
      };

      return newState;
    }

    case SET_COMPILATION_PAGE_TITLE: {
      const newState = {
        ...state,
        title: action.value,
      };

      return newState;
    }

    case SET_LOGO_URL: {
      const newState = {
        ...state,
        logoUrl: action.value,
      };

      return newState;
    }

    case TOGGLE_EDIT_PUBLISHED_VIDEOS_MODE: {
      return {
        ...state,
        isEditPublishedVideosMode: action.value,
      };
    }

    case SET_IS_EDITING_COMPILATION: {
      return {
        ...state,
        editingCompilationPageLink: action.value,
      };
    }

    case SET_API_LOADED_INPUTS: {
      return {
        ...state,
        apiLoadedInputs: {
          title: action.value.title,
          ctaLabel: action.value.ctaLabel,
          ctaLink: action.value.ctaLink,
          ctaDescription: action.value.ctaDescription,
          logoUrl: action.value.logoUrl,
          primaryColor: action.value.primaryColor,
          media: action.value.media
        },
      };
    }

    case SET_MEDIA: {
      return {
        ...state,
        media: action.value,
      };
    }

    case REMOVE_ONE_VIDEO: {
      const videoId = action.value;
      const newList = state.media.filter((m) => m.id !== videoId);
      return {
        ...state,
        media: newList,
      };
    }

    default: {
      return state;
    }
  }
}

// Action Creators
export function loadVideoCompilationPageData(payload) {
  return {
    type: VIDEO_COMPILATION_LOAD,
    payload,
  };
}

export function setSelectedMediaId(mediaId) {
  return {
    type: SET_SELECTED_VIDEO_ID,
    mediaId,
  };
}

export function setFilterTags(tagIds) {
  return {
    type: SET_FILTER_TAGS,
    tagIds,
  };
}

export function setCompilationCtaLabel(label) {
  return {
    type: SET_CTA_LABEL,
    label,
  };
}

export function setCompilationCtaLink(link) {
  return {
    type: SET_CTA_LINK,
    link,
  };
}

export function setCompilationCtaDescription(desc) {
  return {
    type: SET_CTA_DESCRIPTION,
    desc,
  };
}

export function setCompilationPageAutoInclude(value) {
  return {
    type: SET_AUTO_INCLUDE,
    value,
  };
}

export function setCompilationPageEnabled(value) {
  return {
    type: SET_COMPILATION_PAGE_ENABLED,
    value,
  };
}

export function setCompilationPageWebsiteLink(link) {
  return {
    type: SET_COMPILATION_PAGE_WEB_LINK,
    link,
  };
}

export function setCompilationPageEditingMedia(media) {
  return {
    type: SET_COMPILATION_EDIT_MEDIA,
    media,
  };
}

export function setCompilationPageLoading(value) {
  return {
    type: SET_COMPILATION_LOADING,
    value,
  };
}

export function setCompilationPageAutoPlay(value) {
  return {
    type: SET_COMPILATION_AUTO_PLAY,
    value,
  };
}

export function setCompilationPrimaryColor(value) {
  return {
    type: SET_COMPILATION_PRIMARY_COLOR,
    value,
  };
}

export function toggleEditPublishedVideosMode(value) {
  return {
    type: TOGGLE_EDIT_PUBLISHED_VIDEOS_MODE,
    value,
  };
}

export function setEditingCompilationPageLink(value) {
  return {
    type: SET_IS_EDITING_COMPILATION,
    value,
  };
}

export function setCompilationPageTitle(value) {
  return {
    type: SET_COMPILATION_PAGE_TITLE,
    value,
  };
}

export function setCompilationPageLogoUrl(value) {
  return {
    type: SET_LOGO_URL,
    value,
  };
}

export function setApiLoadedInputs(value) {
  return {
    type: SET_API_LOADED_INPUTS,
    value: value.data,
  };
}

export function setVcpMedia(value) {
  return {
    type: SET_MEDIA,
    value,
  };
}

export function removeOneVideo(value) {
  return {
    type: REMOVE_ONE_VIDEO,
    value,
  };
}

// Selectors
export const getVideoCompilationState = createSelector(
  (state) => state,
  (state) => state.get('videoCompilation')
);

export const getVideoCompilationPageLoading = createSelector(
  getVideoCompilationState,
  (videoCompilationState) => videoCompilationState?.isLoading
);

export const getVideoCompilationId = createSelector(
  getVideoCompilationState,
  (videoCompilationState) => videoCompilationState?.id
);

export const getVideoCompilationAutoPlay = createSelector(
  getVideoCompilationState,
  (videoCompilationState) => videoCompilationState?.autoPlay
);

export const getVideoCompilationBranding = createSelector(
  getVideoCompilationState,
  (videoCompilationState) => deep(videoCompilationState, 'campaignBranding')
);

export const getVideoCompilationMedia = createSelector(
  getVideoCompilationState,
  (videoCompilationState) => deep(videoCompilationState, 'media')
);

export const getVideoCompilationHasMedia = createSelector(
  getVideoCompilationState,
  (videoCompilationState) => !!deep(videoCompilationState, 'media')?.length
);

export const getSelectedVideoId = createSelector(
  getVideoCompilationState,
  (videoCompilationState) => deep(videoCompilationState, 'selectedMediaId')
);

export const getVideoCompilationTags = createSelector(
  getVideoCompilationState,
  (videoCompilationState) => deep(videoCompilationState, 'allTags')
);

export const getVideoCompilationFilterTags = createSelector(
  getVideoCompilationState,
  (videoCompilationState) => deep(videoCompilationState, 'filterTags')
);

export const getVideoCompilationPageLink = createSelector(
  getVideoCompilationState,
  (videoCompilationState) => deep(videoCompilationState, 'pageLink')
);

export const getVideoCompilationPageTitle = createSelector(
  getVideoCompilationState,
  (getVideoCompilationState) => deep(getVideoCompilationState, 'title')
);

export const getVideoCompilationPageCtaLink = createSelector(
  getVideoCompilationState,
  (getVideoCompilationState) => deep(getVideoCompilationState, 'ctaLink')
);

export const getVideoCompilationPageCtaLabel = createSelector(
  getVideoCompilationState,
  (getVideoCompilationState) => deep(getVideoCompilationState, 'ctaLabel')
);

export const getVideoCompilationPageAutoInclude = createSelector(
  getVideoCompilationState,
  (getVideoCompilationState) => deep(getVideoCompilationState, 'autoInclude')
);

export const getVideoCompilationPageEnabled = createSelector(
  getVideoCompilationState,
  (getVideoCompilationState) => deep(getVideoCompilationState, 'enabled')
);

export const getVideoCompilationPageCtaDescription = createSelector(
  getVideoCompilationState,
  (getVideoCompilationState) => deep(getVideoCompilationState, 'ctaDescription')
);

export const getVideoCompilationPageCampaignName = createSelector(
  getVideoCompilationState,
  (getVideoCompilationState) => deep(getVideoCompilationState, 'campaignName')
);

export const getVideoCompilationPageWebsiteLink = createSelector(
  getVideoCompilationState,
  (getVideoCompilationState) => deep(getVideoCompilationState, 'websiteLink')
);

export const getVideoCompilationSelectedEditMedia = createSelector(
  getVideoCompilationState,
  (getVideoCompilationState) => deep(getVideoCompilationState, 'editingMedia')
);

export const getVideoCompilationPrimaryColor = createSelector(
  getVideoCompilationState,
  (getVideoCompilationState) =>
    deep(getVideoCompilationState, 'primaryColor') || 'var(--primary-600)'
);

export const getIsEditPublishedVideosMode = createSelector(
  getVideoCompilationState,
  (getVideoCompilationState) => deep(getVideoCompilationState, 'isEditPublishedVideosMode')
);

export const getEditingCompilationPageLink = createSelector(
  getVideoCompilationState,
  (getVideoCompilationState) => deep(getVideoCompilationState, 'editingCompilationPageLink')
);

export const getVideoCompilationPageLogoUrl = createSelector(
  getVideoCompilationState,
  (getVideoCompilationState) => deep(getVideoCompilationState, 'logoUrl')
);

export const getApiLoadedInputs = createSelector(
  getVideoCompilationState,
  (getVideoCompilationState) => deep(getVideoCompilationState, 'apiLoadedInputs')
);

export const getVideoCompilationPageBrandingBackgroundColor = createSelector(
  getVideoCompilationBranding,
  (videoCompilationBranding) => deep(videoCompilationBranding, 'banner') || 'var(--primary-600)'
);

export const getVideoCompilationPageBrandingColor = createSelector(
  getVideoCompilationBranding,
  (videoCompilationBranding) => {
    const banner = deep(videoCompilationBranding, 'banner');
    if (!banner) return 'rgb(255 255 255 / 1)';

    return deep(videoCompilationBranding, 'fontColor') || 'rgb(255 255 255 / 1)';
  }
);

export const getNextMediaId = createSelector(
  [getVideoCompilationMedia, getSelectedVideoId],
  (mediaArray, selectedMediaId) => {
    if (mediaArray && selectedMediaId) {
      const currentIndex = mediaArray.findIndex((media) => media.id === selectedMediaId);

      // if there's a next media in the array, return its id, otherwise return false
      if (currentIndex !== -1 && currentIndex < mediaArray.length - 1) {
        return mediaArray[currentIndex + 1].id;
      }
    }

    return false;
  }
);

export const getVideoCompilationSettingsProgress = createSelector(
  getVideoCompilationState,
  (getVideoCompilationState) => {
    const recommendedFields = ['title', 'ctaLink', 'ctaDescription', 'media', 'logoUrl'];

    const defaultDescription =
      'Please add a short description to help your audience understand the context of the videos being showed.';

    const numberOfFilledInputs = recommendedFields.reduce((count, value) => {
      if (value === 'ctaDescription') {
        return getVideoCompilationState[value] === defaultDescription ||
          getVideoCompilationState[value] === ''
          ? count
          : count + 1;
      }
      if (value === 'media') {
        return getVideoCompilationState[value].length ? count + 1 : count;
      }
      if (getVideoCompilationState[value]) return count + 1;
      return count;
    }, 0);

    const percentageOfFilledInputs = (numberOfFilledInputs * 100) / recommendedFields.length;

    return percentageOfFilledInputs;
  }
);

export const getVideoCompilationIsBrandingSettingsComplete = createSelector(
  getVideoCompilationState,
  (getVideoCompilationState) => {
    const recommendedFields = ['logoUrl'];
    const numberOfFilledInputs = recommendedFields.reduce((count, value) => {
      if (getVideoCompilationState[value]) return count + 1;
      return count;
    }, 0);

    return numberOfFilledInputs === recommendedFields.length;
  }
);

export const getVideoCompilationIsInformationSettingsComplete = createSelector(
  getVideoCompilationState,
  (getVideoCompilationState) => {
    const recommendedFields = ['title', 'ctaLink', 'ctaDescription'];

    const defaultDescription =
      'Please add a short description to help your audience understand the context of the videos being showed.';

    const numberOfFilledInputs = recommendedFields.reduce((count, value) => {
      if (value === 'ctaDescription') {
        return getVideoCompilationState[value] === defaultDescription ||
          getVideoCompilationState[value] === ''
          ? count
          : count + 1;
      }
      if (getVideoCompilationState[value]) return count + 1;
      return count;
    }, 0);

    return numberOfFilledInputs === recommendedFields.length;
  }
);

export const getVideoCompilationIsVideoSettingsComplete = createSelector(
  getVideoCompilationState,
  (getVideoCompilationState) => {
    return !!deep(getVideoCompilationState, 'media').length;
  }
);

export const getVideoCompilationStateIsDirty = createSelector(
  getVideoCompilationState,
  (getVideoCompilationState) => {
    const apiLoadedInputs = deep(getVideoCompilationState, 'apiLoadedInputs');

    const clientStateInputs = {
      title: deep(getVideoCompilationState, 'title'),
      ctaLabel: deep(getVideoCompilationState, 'ctaLabel'),
      ctaLink: deep(getVideoCompilationState, 'ctaLink'),
      ctaDescription: deep(getVideoCompilationState, 'ctaDescription'),
      logoUrl: deep(getVideoCompilationState, 'logoUrl'),
      primaryColor: deep(getVideoCompilationState, 'primaryColor'),
      media: deep(getVideoCompilationState, 'media'),
    };

    return !deepEquals(apiLoadedInputs, clientStateInputs) 
  }
);



// WEBPACK FOOTER //
// ./src/redux/modules/videoCompilationPage.js